/**
 * The Minimise / Restore animations. Minimise: the record editor's window
 * shrinks and drops into its place among the Open editors at the bottom of
 * the screen — the chip in the bottom tray (classic / rail / mega menu / flat)
 * or the editor's tile in the Dock (desktop). A layout with no such place on
 * screen (its editors live in a menu) gets the same shrink-and-drop to the
 * bottom centre. Restore is the same path backwards: the window rises out of
 * its chip and grows to its size.
 *
 * Presentation only, and never in the way:
 *   - minimise: the real editor hides at once, exactly as before (it stays
 *     MOUNTED; this animates a detached, inert COPY of its window), and the
 *     copy carries no ids and no `data-tw-*` hooks, so nothing — a test case
 *     included — can find it, and it ignores the pointer;
 *   - restore: the real window is shown at once and animates in place from
 *     its very first frame (it is committed synchronously, so it is never
 *     painted full size before the animation starts — that was the flicker);
 *   - reduced motion, a browser without the Web Animations API, or any error
 *     ⇒ no animation, and the copy is always removed.
 *
 * A layout marks where a session lands with `data-tw-minimise-target="<id>"`
 * (`MINIMISE_TARGET_ATTR`).
 */
import { flushSync } from 'react-dom'
import { Z_RECORD_EDITOR } from '../../layers'

export const MINIMISE_TARGET_ATTR = 'data-tw-minimise-target'

const DURATION_MS = 420
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'
/** Restore is a little quicker: the person is waiting to type. */
const RESTORE_MS = 340
const RESTORE_EASING = 'cubic-bezier(0.2, 0, 0, 1)'
/** The landing box when no target is on screen: a chip-sized box near the
 *  bottom edge, centred. */
const FALLBACK = { width: 220, height: 44, bottomGap: 14 }

function reducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  } catch {
    return false
  }
}

function canAnimate(): boolean {
  return typeof document !== 'undefined' && !reducedMotion()
    && typeof Element.prototype.animate === 'function'
}

/** The frame halfway between the window and its chip — shared by both
 *  directions so restore retraces minimise exactly. `d` / `s` are the
 *  translation and scale that put the window on its chip. */
function midFrame(dx: number, dy: number, sx: number, sy: number): string {
  return `translate(${dx * 0.35}px, ${dy * 0.2}px) scale(${0.55 + sx * 0.45}, ${0.55 + sy * 0.45})`
}

/** The dimmed backdrop a modal window sits on (a fixed, coloured parent). */
function backdropOf(card: HTMLElement): { host: HTMLElement; color: string } | null {
  const host = card.parentElement
  if (!host) return null
  const cs = window.getComputedStyle(host)
  if (cs.position !== 'fixed' || !cs.backgroundColor || cs.backgroundColor === 'rgba(0, 0, 0, 0)') return null
  return { host, color: cs.backgroundColor }
}

/** The record editor window now on screen (a hidden — minimised — one drops
 *  its `data-tw-dialog`, so only the shown one matches). */
function visibleEditorCard(): HTMLElement | null {
  const cards = document.querySelectorAll<HTMLElement>('[data-tw-dialog="editor"]')
  for (const el of Array.from(cards)) {
    const r = el.getBoundingClientRect()
    if (r.width > 0 && r.height > 0) return el
  }
  return null
}

/** Where the session lands: its marked chip / tile when it is on screen. */
function landingRect(sessionId: string): DOMRect {
  const sel = `[${MINIMISE_TARGET_ATTR}="${CSS.escape(sessionId)}"]`
  for (const el of Array.from(document.querySelectorAll<HTMLElement>(sel))) {
    const r = el.getBoundingClientRect()
    const onScreen = r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight
    if (onScreen) return r
  }
  const w = Math.min(FALLBACK.width, window.innerWidth - 32)
  return new DOMRect(
    (window.innerWidth - w) / 2, window.innerHeight - FALLBACK.height - FALLBACK.bottomGap,
    w, FALLBACK.height,
  )
}

/** A detached copy of the window: no ids, no hooks, no focus, no pointer. */
function inertCopy(card: HTMLElement, from: DOMRect): HTMLElement {
  const copy = card.cloneNode(true) as HTMLElement
  for (const el of [copy, ...Array.from(copy.querySelectorAll<HTMLElement>('*'))]) {
    el.removeAttribute('id')
    el.removeAttribute('name')
    for (const a of Array.from(el.attributes)) {
      if (a.name.startsWith('data-tw')) el.removeAttribute(a.name)
    }
  }
  copy.setAttribute('aria-hidden', 'true')
  copy.setAttribute('inert', '')
  Object.assign(copy.style, {
    position: 'fixed', left: `${from.left}px`, top: `${from.top}px`,
    width: `${from.width}px`, height: `${from.height}px`, margin: '0',
    zIndex: String(Z_RECORD_EDITOR), pointerEvents: 'none',
    transformOrigin: '0 0', willChange: 'transform, opacity', display: 'flex',
  } satisfies Partial<CSSStyleDeclaration>)
  return copy
}

/** The modal's dimmed backdrop, faded out rather than cut (when the window
 *  sat on one). */
function fadingBackdrop(card: HTMLElement): HTMLElement | null {
  const bd = backdropOf(card)
  if (!bd) return null
  const veil = document.createElement('div')
  veil.setAttribute('aria-hidden', 'true')
  Object.assign(veil.style, {
    position: 'fixed', inset: '0', background: bd.color,
    zIndex: String(Z_RECORD_EDITOR - 1), pointerEvents: 'none',
  } satisfies Partial<CSSStyleDeclaration>)
  return veil
}

/** A small bump on the chip the window landed in. */
function pulse(sessionId: string): void {
  const el = document.querySelector<HTMLElement>(`[${MINIMISE_TARGET_ATTR}="${CSS.escape(sessionId)}"]`)
  el?.animate?.(
    [{ transform: 'scale(1)' }, { transform: 'scale(1.07)' }, { transform: 'scale(1)' }],
    { duration: 260, easing: 'ease-out' },
  )
}

/**
 * Call BEFORE the editor is hidden (it copies what is on screen); `hide` then
 * runs at once, and the copy flies to where `sessionId` lands once the
 * layout has drawn it (the tray / Dock re-render after the hide).
 */
export function minimiseWithAnimation(sessionId: string | null | undefined, hide: () => void): void {
  let copy: HTMLElement | null = null
  let veil: HTMLElement | null = null
  const cleanup = () => { copy?.remove(); veil?.remove(); copy = null; veil = null }
  try {
    const card = sessionId && canAnimate() ? visibleEditorCard() : null
    if (card) {
      const from = card.getBoundingClientRect()
      veil = fadingBackdrop(card)
      copy = inertCopy(card, from)
      if (veil) document.body.appendChild(veil)
      document.body.appendChild(copy)
    }
  } catch (err) {
    console.warn('[shell] minimise animation unavailable', err)
    cleanup()
  }
  hide()
  if (!copy || !sessionId) return
  // Whatever happens below, the copy never outlives the animation.
  const guard = window.setTimeout(cleanup, DURATION_MS + 400)
  // Two frames: React commits the hide, then the tray / Dock draws the chip.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    try {
      if (!copy) return
      const from = copy.getBoundingClientRect()
      const to = landingRect(sessionId)
      const sx = Math.max(to.width / from.width, 0.02)
      const sy = Math.max(to.height / from.height, 0.02)
      const dx = to.left - from.left
      const dy = to.top - from.top
      veil?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: DURATION_MS * 0.7, easing: 'ease-out', fill: 'forwards' })
      const run = copy.animate([
        { transform: 'translate(0px, 0px) scale(1, 1)', opacity: 1, offset: 0 },
        // Shrinks first, then drops: reads as "went down to the bottom".
        { transform: midFrame(dx, dy, sx, sy), opacity: 0.95, offset: 0.4 },
        { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, opacity: 0.4, offset: 1 },
      ], { duration: DURATION_MS, easing: EASING, fill: 'forwards' })
      run.onfinish = () => { window.clearTimeout(guard); cleanup(); pulse(sessionId) }
      run.oncancel = () => { window.clearTimeout(guard); cleanup() }
    } catch (err) {
      console.warn('[shell] minimise animation failed', err)
      window.clearTimeout(guard)
      cleanup()
    }
  }))
}

/**
 * Restore: `show` runs at once (committed synchronously), then the real
 * window — now on screen — rises from where `sessionId` sits among the Open
 * editors and grows to its size. Call it in place of `show()`; it never
 * delays or skips it.
 */
export function restoreWithAnimation(sessionId: string, show: () => void): void {
  let from: DOMRect | null = null
  try {
    // Where it comes from must be read BEFORE the restore (the chip may
    // restyle or leave the tray once its editor is on screen).
    if (canAnimate() && !visibleEditorCard()) from = landingRect(sessionId)
  } catch (err) {
    console.warn('[shell] restore animation unavailable', err)
  }
  if (!from) { show(); return }
  try {
    // Commit now, so the animation below is in place before the browser
    // paints the window for the first time.
    flushSync(show)
  } catch (err) {
    console.warn('[shell] restore could not be committed synchronously', err)
    show()
    return
  }
  try {
    const card = visibleEditorCard()
    if (!card) return
    const to = card.getBoundingClientRect()
    if (!to.width || !to.height) return
    const sx = Math.max(from.width / to.width, 0.02)
    const sy = Math.max(from.height / to.height, 0.02)
    const dx = from.left - to.left
    const dy = from.top - to.top
    card.animate([
      { transformOrigin: '0 0', transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, opacity: 0.4, offset: 0 },
      // Rises first, then grows: the minimise path backwards.
      { transformOrigin: '0 0', transform: midFrame(dx, dy, sx, sy), opacity: 0.95, offset: 0.6 },
      { transformOrigin: '0 0', transform: 'translate(0px, 0px) scale(1, 1)', opacity: 1, offset: 1 },
    ], { duration: RESTORE_MS, easing: RESTORE_EASING })
    const bd = backdropOf(card)
    bd?.host.animate(
      [{ backgroundColor: 'rgba(0, 0, 0, 0)' }, { backgroundColor: bd.color }],
      { duration: RESTORE_MS * 0.8, easing: 'ease-out' },
    )
  } catch (err) {
    // The window is already shown; only the flourish is lost.
    console.warn('[shell] restore animation failed', err)
  }
}
